import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { css } from '@emotion/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { EmailOutlined } from '@mui/icons-material';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import Card from '@mui/material/Card';
import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';

import { createSupportTicket } from '~/api/account';
import contactUsImage from '~/assets/images/contact-us.webp';
import FooterPublic from '~/components/organisms/FooterPublic/FooterPublic';
import HeaderPublic from '~/components/organisms/HeaderPublic/HeaderPublic';
import { Button } from '~/components/ui/button';
import { StyledCustomLoader } from '~/components/ui/CustomLoader';
import { Text } from '~/components/ui/text';
import TextInput from '~/components/ui/TextInput';
import { useToastStore } from '~/store/useToastStore';
import { COLORS } from '~/styles/colors';

export default function ContactUs() {
  const { t } = useTranslation();
  const styles = useStyles();
  const contactUsSchema = z.object({
    name: z
      .string({ required_error: t('inputs:required') })
      .min(1, { message: t('inputs:required') })
      .max(100, { message: t('errors:max', { count: 100 }) }),
    email: z
      .string({ required_error: t('inputs:required') })
      .email({ message: t('errors:emailIsValid') })
      .max(300, { message: t('errors:max', { count: 300 }) }),
    subject: z
      .string({ required_error: t('inputs:required') })
      .min(1, { message: t('inputs:required') })
      .max(100, { message: t('errors:max', { count: 100 }) }),
    message: z
      .string({ required_error: t('inputs:required') })
      .min(1, { message: t('inputs:required') })
      .max(300, { message: t('errors:max', { count: 300 }) }),
  });
  type ContactUsSchema = z.infer<typeof contactUsSchema>;
  const theme = useTheme();
  const isSmallDesktopOrAbove = useMediaQuery(theme.breakpoints.up('lg'));
  const { setShow } = useToastStore();

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
  } = useForm({
    resolver: zodResolver(contactUsSchema),
  });

  const { mutate: createSupportTicketMutate, isPending: isPendingCreateSupportTicket } = useMutation({
    mutationFn: createSupportTicket,
    onSuccess: () => {
      reset({
        name: '',
        email: '',
        subject: '',
        message: '',
      });
      setShow({
        show: true,
        type: 'success',
        message: 'Support ticket was created successfully',
      });
    },
    onError: () => {
      setShow({
        show: true,
        type: 'error',
        message: 'Support ticket was created successfully',
      });
    },
  });

  const contact = (values: ContactUsSchema) => {
    if (!isPendingCreateSupportTicket) {
      void createSupportTicketMutate(values);
    }
  };

  return (
    <Box sx={styles.Container}>
      <HeaderPublic />
      <Box sx={styles.Content}>
        {isSmallDesktopOrAbove && <img src={contactUsImage} alt="Contact us image" width={547} height={550} />}
        <Card sx={styles.StyledCard}>
          <Text variant="h2">{t('contactUs:title')}</Text>
          <Box sx={styles.Form}>
            <Box>
              <Text sx={{ marginBottom: '12px' }}>{t('contactUs:nameSurname')}</Text>
              <Controller
                control={control}
                name="name"
                render={({ field: { value, onChange } }) => {
                  return (
                    <TextInput
                      icon={<PersonOutlineOutlinedIcon sx={{ color: COLORS.primary }} />}
                      error={errors.name?.message}
                      helperText={errors.name?.message}
                      placeholder={t('contactUs:nameSurnameEg')}
                      onChange={onChange}
                      value={value}
                      type="text"
                      fullWidth
                    />
                  );
                }}
              />
            </Box>
            <Box>
              <Text sx={{ marginBottom: '12px' }}>{t('contactUs:email')}</Text>
              <Controller
                control={control}
                name="email"
                render={({ field: { value, onChange } }) => {
                  return (
                    <TextInput
                      icon={<EmailOutlined sx={{ color: COLORS.primary }} />}
                      value={value}
                      error={errors.email?.message}
                      onChange={onChange}
                      helperText={errors.email?.message}
                      placeholder={t('contactUs:emailEg')}
                      type="email"
                      fullWidth
                    />
                  );
                }}
              />
            </Box>
            <Box>
              <Text sx={{ marginBottom: '12px' }}>{t('contactUs:subject')}</Text>
              <Controller
                control={control}
                name="subject"
                render={({ field: { value, onChange } }) => {
                  return (
                    <TextInput
                      value={value}
                      onChange={onChange}
                      error={errors.subject?.message}
                      helperText={errors.subject?.message}
                      placeholder={t('contactUs:subjectPlaceholder')}
                      type="email"
                      fullWidth
                    />
                  );
                }}
              />
            </Box>
            <Box>
              <Text sx={{ marginBottom: '12px' }}>{t('contactUs:question')}</Text>
              <Controller
                control={control}
                name="message"
                render={({ field: { value, onChange } }) => {
                  return (
                    <TextInput
                      value={value}
                      onChange={onChange}
                      multiline
                      error={errors.message?.message}
                      helperText={errors.message?.message}
                      minRows={3}
                      InputPropsSx={{ borderRadius: '20px' }}
                      fullWidth
                      placeholder={t('contactUs:questionEg')}
                    />
                  );
                }}
              />
            </Box>
            <Button onClick={handleSubmit(contact)} sx={styles.StyledButton}>
              {isPendingCreateSupportTicket ? <StyledCustomLoader /> : t('contactUs:send')}
            </Button>
          </Box>
        </Card>
      </Box>
      <FooterPublic />
    </Box>
  );
}

const useStyles = () => {
  const theme = useTheme();
  return {
    Container: css`
      display: flex;
      flex-direction: column;
    `,
    Content: css`
      padding: 16px;
      justify-content: center;
      display: flex;
      gap: 8px;

      ${theme.breakpoints.up('md')} {
        padding: 108px;
        gap: 108px;
      }
    `,

    StyledCard: css`
      padding: 8px;
      flex: 1;
      max-width: 545px;
      border-radius: 20px;
      box-shadow: 0 0 20px 0 #0000000d;
      ${theme.breakpoints.up('md')} {
        padding: 30px;
      }
    `,
    Form: css`
      margin-top: 20px;
      display: flex;
      flex-direction: column;
      gap: 16px;
    `,
    StyledButton: css`
      width: fit-content;
      margin: 0 auto;
    `,
  };
};
